import _ from 'lodash'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { usePatientIdentifierLabel } from '@/hooks/usePatientIdentifierLabel'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import { Trans } from '@lingui/macro'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import Accordion from '@/components/ui/accordions/Accordion'
import DatePickerField from '@/components/formik/fields/DatePickerField'
import PhoneNumberField from '@/components/formik/fields/PhoneNumberField'
import SelectField from '@/components/formik/fields/SelectField'
import TextField from '@/components/formik/fields/TextField'

import { ParentsFieldArray } from '@/pages/patient/ui/ParentsFieldArray'

import ContentHeader from './ContentHeader'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { calculateAge } from '@/utils/patient'
import TextInput from '@/components/ui/inputs/TextInput'
import { useIsUS } from '@/hooks/useIsUS'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  surname: yup.string().required(),
  email: yup.string().email().optional(),
  birthday: yup.date().max(new Date()),
  notes: yup.string().optional(),
})

const blackListedLanguagesForCarteVitale = ['en', 'en-US', 'en-SA', 'ar-SA']

// Patients pre-created without a NIR are stored with an all-zeros placeholder ('000000000000000').
export const isSecuMissing = (secu?: string | null) => !secu || /^0+$/.test(secu.replace(/\s/g, ''))

export type PatientInfoProps = {
  onGetCarteVitale: () => void
  infos: {
    name: string
    surname: string
    email: string
    gender: string
    phone: string
    birthday: Date | null
    secu: string
    codeGestion: string
    codeRegime: string
    caisse: string
    notes: string
    pluginId: string
    background: string
    treatment: string
    parents: Array<{
      firstname: string
      lastname: string
      email?: string
      phone?: string
      city?: string
      cp?: string
    }>
    privateDoctor: PatientInfoProps['infos']['parents'][number] | null
    insured: string
    insuredRelationship: string
    insuranceProvider: string
    primaryInsurance: string
    secondaryInsurance: string
    primaryPolicy: string
    secondaryPolicy: string
    primaryPharmacy: string
  }
  isModifying?: boolean
  onModifyPatient: (values: PatientInfoProps['infos']) => void
  readOnly?: boolean
  carteVitaleSuccess?: boolean
  showVitaleCard?: boolean
}

export function PatientInfo(props: PatientInfoProps) {
  const { infos, isModifying = false, onModifyPatient, onGetCarteVitale, readOnly = false, carteVitaleSuccess = false, showVitaleCard = false } = props
  const { t, i18n } = useTranslation()
  const identifierLabel = usePatientIdentifierLabel()
  const isTooSmall = useIsMobile()
  const xs = isTooSmall ? 12 : 6
  const isUS = useIsUS()
  const showVitaleCardButton = showVitaleCard && !readOnly && !blackListedLanguagesForCarteVitale.includes(i18n.language)
  const showSecuMissingHint = showVitaleCardButton && !isUS && isSecuMissing(infos.secu)

  const genderOptions = useMemo(
    () => [
      { label: t('field.gender.options.male.label'), value: 'M' },
      { label: t('field.gender.options.female.label'), value: 'F' },
      { label: t('field.gender.options.unknown.label'), value: 'I' },
    ],
    [t]
  )

  return (
    <Container maxWidth="md" sx={{ pt: '20px', pb: '20px' }}>
      <Paper>
        <Stack sx={{ px: 3 }} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <ContentHeader sx={{ p: '20px 16px' }} title={_.trim(`${infos.surname} ${infos.name}`)} subtitle={t('page.liveConsultation.patientInfo.title')} />
          {showVitaleCardButton && (
            <Button sx={{ height: '40px' }} color={showSecuMissingHint ? 'warning' : 'secondary'} size="small" onClick={onGetCarteVitale} variant="contained">
              {t('page.createPatient.addPatientByCarteVitaleButton.label')}
            </Button>
          )}
        </Stack>
        {showSecuMissingHint && (
          <Alert severity="warning" sx={{ mx: 3, mb: 2 }}>
            <Trans>Social security number missing: read the carte Vitale, then click Modify to save it.</Trans>
          </Alert>
        )}
        <Divider />
        <Stack spacing={2} sx={{ p: '20px 16px' }}>
          <Formik
            enableReinitialize
            initialValues={infos}
            onSubmit={onModifyPatient}
            validationSchema={() =>
              yup.lazy((formikValues: PatientInfoProps['infos']) => {
                return !formikValues.privateDoctor
                  ? validationSchema
                  : validationSchema.shape({
                      privateDoctor: yup.object().shape({
                        firstname: yup.string().required(),
                        lastname: yup.string().required(),
                        email: yup.string().email(),
                      }),
                    })
              })
            }
          >
            {(formikProps) => (
              <Form onSubmit={formikProps.handleSubmit}>
                {!readOnly && (
                  <Stack direction="row" justifyContent="end" spacing={2}>
                    <Button onClick={formikProps.handleReset} size="small" variant="neutral">
                      {t('common:button.reset.label')}
                    </Button>
                    <LoadingButton
                      disabled={!carteVitaleSuccess && (!formikProps.isValid || !formikProps.dirty)}
                      loading={isModifying}
                      size="small"
                      type="submit"
                      variant="contained"
                    >
                      {t('common:button.modify.label')}
                    </LoadingButton>
                  </Stack>
                )}
                <Grid container rowSpacing={3} columnSpacing={isTooSmall ? 2 : 4}>
                  <Grid item xs={12}>
                    <TextField fullWidth label={identifierLabel} name={isUS ? 'primaryPolicy' : 'secu'} readOnly={readOnly} />
                  </Grid>
                  {!isUS && (
                    <Grid item xs={12}>
                      <TextField fullWidth label={t('field.id.label')} name="pluginId" readOnly={readOnly} />
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.lastName.label')} name="surname" readOnly={readOnly} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.firstName.label')} name="name" readOnly={readOnly} />
                  </Grid>
                  {isUS && (
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <span>{t('field.insured.label')}</span>
                            <Tooltip placement="top" title={<Trans>(if different from the patient name)</Trans>}>
                              <InfoOutlinedIcon fontSize="small" />
                            </Tooltip>
                          </Stack>
                        }
                        name="insured"
                        readOnly={readOnly}
                      />
                    </Grid>
                  )}
                  <Grid item xs={xs}>
                    <TextField fullWidth label={t('field.email.label')} name="email" readOnly={readOnly} />
                  </Grid>
                  <Grid item xs={isTooSmall ? 5 : 6}>
                    <SelectField fullWidth label={t('field.gender.label')} name="gender" options={genderOptions} readOnly={readOnly} />
                  </Grid>
                  <Grid item xs={isTooSmall ? 7 : 6}>
                    <DatePickerField disableFuture fullWidth label={t('field.birthday.label')} name="birthday" readOnly={readOnly} forceUtc />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      fullWidth
                      label={t('field.age.label')}
                      name="age"
                      value={formikProps.values.birthday ? calculateAge(formikProps.values.birthday) : ''}
                      readOnly
                    />
                  </Grid>
                  <Grid item xs={xs}>
                    <PhoneNumberField label={t('field.phone.label')} name="phone" readOnly={readOnly} />
                  </Grid>
                  <Grid item xs={xs}>
                    {isUS ? (
                      <TextField fullWidth label={t('field.insuranceProvider.label')} name="insuranceProvider" readOnly={readOnly} />
                    ) : (
                      <TextField fullWidth label={t('field.codeGestion.label')} name="codeGestion" readOnly={readOnly} />
                    )}
                  </Grid>
                  <Grid item xs={xs}>
                    {isUS ? (
                      <TextField fullWidth label={t('field.primaryInsurance.label')} name="primaryInsurance" readOnly={readOnly} />
                    ) : (
                      <TextField fullWidth label={t('field.codeRegime.label')} name="codeRegime" readOnly={readOnly} />
                    )}
                  </Grid>
                  <Grid item xs={xs}>
                    {isUS ? (
                      <TextField fullWidth label={t('field.insuredRelationship.label')} name="insuredRelationship" readOnly={readOnly} />
                    ) : (
                      <TextField fullWidth label={t('field.caisse.label')} name="caisse" readOnly={readOnly} />
                    )}
                  </Grid>
                  {isUS && (
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label={t('field.secondaryInsurance.label')} name="secondaryInsurance" readOnly={readOnly} />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField multiline fullWidth label={t('field.note.label', { count: 2 })} name="notes" readOnly={readOnly} />
                    {formikProps.values.notes !== formikProps.initialValues.notes && !readOnly && (
                      <Button onClick={(e: React.MouseEvent) => formikProps.handleSubmit()} size="small" variant="contained" sx={{ mt: 1 }}>
                        {t('common:button.save.label')}
                      </Button>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField multiline fullWidth label={t('field.treatment.label', { count: 2 })} name="treatment" readOnly={readOnly} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField multiline fullWidth label={t('field.background.label', { count: 2 })} name="background" readOnly={readOnly} />
                  </Grid>
                  {isUS && (
                    <Grid item xs={12}>
                      <TextField multiline fullWidth label={t('field.primaryPharmacy.label')} name="primaryPharmacy" readOnly={readOnly} />
                    </Grid>
                  )}
                </Grid>
                <Box sx={{ mt: '16px' }}>
                  <Accordion title={t('page.createPatient.createPatientForm.privateDoctor.label')}>
                    <Stack alignItems="center" spacing={2}>
                      {formikProps.values.privateDoctor && (
                        <Grid container spacing={3} sx={{ mt: '0px', mb: 2 }}>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth label={t('field.firstName.label')} name="privateDoctor.firstname" readOnly={readOnly} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth label={t('field.lastName.label')} name="privateDoctor.lastname" readOnly={readOnly} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth label={t('field.email.label')} name="privateDoctor.email" readOnly={readOnly} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <PhoneNumberField label={t('field.phone.label')} name="privateDoctor.phone" readOnly={readOnly} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth label={t('field.city.label')} name="privateDoctor.city" readOnly={readOnly} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth label={t('field.postalCode.label')} name="privateDoctor.cp" readOnly={readOnly} />
                          </Grid>
                        </Grid>
                      )}
                      {formikProps.values.privateDoctor && !infos.privateDoctor && !readOnly && (
                        <Button
                          color="error"
                          onClick={() => {
                            formikProps.setFieldValue('privateDoctor', null)
                          }}
                          size="small"
                          variant="outlined"
                        >
                          {t('common:button.delete.label')}
                        </Button>
                      )}
                      {!formikProps.values.privateDoctor && !infos.privateDoctor && !readOnly && (
                        <Button
                          onClick={() => {
                            formikProps.setFieldValue('privateDoctor', {
                              firstname: '',
                              lastname: '',
                              email: '',
                              phone: '',
                              city: '',
                              cp: '',
                            })
                          }}
                          size="small"
                          variant="contained"
                        >
                          {t('common:button.add.label')}
                        </Button>
                      )}
                    </Stack>
                  </Accordion>
                </Box>
                <Box sx={{ mt: '16px' }}>
                  <ParentsFieldArray readOnly={readOnly} />
                </Box>
              </Form>
            )}
          </Formik>
        </Stack>
      </Paper>
    </Container>
  )
}

export default PatientInfo
